import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Logo } from '../../components/Logo/Logo';
import { apiService } from '../../services/api';
import './AcceptInvite.css';

interface InviteInfo {
  email: string;
  nome?: string;
  role: string;
  expiresAt: string;
}

interface OrganizationInfo {
  name: string;
  slug: string;
}

export const AcceptInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [organization, setOrganization] = useState<OrganizationInfo | null>(null);
  const [form, setForm] = useState({
    nome: '',
    senha: '',
    confirmSenha: '',
  });

  useEffect(() => {
    const loadInvite = async () => {
      if (!token) {
        setError('Token de convite invalido');
        setLoading(false);
        return;
      }

      try {
        const data = await apiService.getInvite(token);
        setInvite(data.invite);
        setOrganization(data.organization);
        setForm(prev => ({ ...prev, nome: data.invite.nome || '' }));
      } catch (err: any) {
        const message = err.response?.data?.message || 'Convite invalido ou expirado';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    loadInvite();
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.nome.trim()) {
      setError('Nome e obrigatorio');
      return;
    }

    if (form.senha.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (form.senha !== form.confirmSenha) {
      setError('As senhas nao conferem');
      return;
    }

    setSubmitting(true);

    try {
      const response = await apiService.acceptInvite(token!, form.senha, form.nome);

      // Save auth data
      localStorage.setItem('token', response.token);
      localStorage.setItem('usuario', JSON.stringify(response.user));
      localStorage.setItem('organization', JSON.stringify(response.organization));

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err: any) {
      const message = err.response?.data?.message || 'Erro ao aceitar convite';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="accept-invite-page">
        <div className="accept-invite-container">
          <div className="accept-invite-loading">
            <Logo size="small" showText={false} logoName="logo2.png" />
            <p>Carregando convite...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="accept-invite-page">
        <div className="accept-invite-container">
          <div className="accept-invite-error-page">
            <Logo size="small" showText={false} logoName="logo2.png" />
            <h1>Convite Invalido</h1>
            <p>{error}</p>
            <Link to="/login" className="btn btn-primary">
              Ir para Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-invite-page">
      <div className="accept-invite-container">
        <div className="accept-invite-card">
          <div className="accept-invite-header">
            <Link to="/">
              <Logo size="small" showText={false} logoName="logo2.png" />
            </Link>
            <h1>Aceitar Convite</h1>
            <p>
              Voce foi convidado para participar de <strong>{organization?.name}</strong>
            </p>
          </div>

          <div className="invite-details">
            <div className="invite-detail">
              <span className="label">Email:</span>
              <span className="value">{invite?.email}</span>
            </div>
            <div className="invite-detail">
              <span className="label">Funcao:</span>
              <span className="value role-badge">{invite?.role === 'admin' ? 'Administrador' : 'Usuario'}</span>
            </div>
          </div>

          {error && (
            <div className="accept-invite-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="nome">Seu nome</label>
              <input
                type="text"
                id="nome"
                name="nome"
                value={form.nome}
                onChange={handleChange}
                placeholder="Como voce quer ser chamado"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="senha">Criar senha</label>
              <input
                type="password"
                id="senha"
                name="senha"
                value={form.senha}
                onChange={handleChange}
                placeholder="Minimo 6 caracteres"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmSenha">Confirmar senha</label>
              <input
                type="password"
                id="confirmSenha"
                name="confirmSenha"
                value={form.confirmSenha}
                onChange={handleChange}
                placeholder="Repita a senha"
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={submitting}
            >
              {submitting ? 'Criando conta...' : 'Aceitar e Criar Conta'}
            </button>
          </form>

          <p className="accept-invite-login">
            Ja tem uma conta? <Link to="/login">Fazer login</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
